'use client';

import { type ComponentProps, type ReactNode, useId } from 'react';

import { tv } from '../../lib/tv';
import { Checkbox as CheckboxPrimitive } from '../ui/checkbox';

export type CheckboxProps = ComponentProps<typeof CheckboxPrimitive> & {
  /** Visible label, clickable. Omit only when a label is provided elsewhere (aria-label). */
  label?: ReactNode;
};

const checkbox = tv({
  slots: {
    root: 'inline-flex items-center gap-2',
    label:
      'text-body-md text-text-primary peer-data-disabled:text-text-disabled cursor-pointer peer-data-disabled:cursor-not-allowed',
  },
});

/**
 * Multi-select and opt-ins (Figma: Components ↳ Controls ↳ Checkbox).
 * `checked` may be `true`, `false` or `"indeterminate"` (partial parent selection).
 */
export function Checkbox({ label, id, className, ...props }: CheckboxProps) {
  const generated = useId();
  const styles = checkbox();
  if (!label) return <CheckboxPrimitive id={id} className={className} {...props} />;
  const controlId = id ?? generated;
  return (
    <span data-slot="checkbox-field" className={styles.root({ className })}>
      <CheckboxPrimitive id={controlId} {...props} />
      <label
        htmlFor={controlId}
        className={styles.label()}
        data-disabled={props.disabled || undefined}
      >
        {label}
      </label>
    </span>
  );
}
